/**
 * secureVault — the refresh token, encrypted at rest behind a per-unlock key.
 *
 * Posture A: only the refresh token is encrypted here. The short-lived access
 * token lives in plaintext localStorage (config.auth.storageKey) for the many
 * synchronous readers. The vault is WRITE-ONCE at enrollment (fixed refresh
 * token); unlock decrypts it to drive POST /auth/refresh on reconnect.
 *
 * Key derivation:
 *   - PRF:  deriveKeyFromPRF(prfOutput)        — biometric unlock (Task 7)
 *   - PIN:  deriveKeyFromPassphrase(pin, salt) — fallback, PBKDF2 600k
 *
 * PIN attempts are rate-limited: maxPinAttempts wrong tries wipes the ciphertext
 * (capture data in DryStore is untouched — only the saved session dies).
 */
import {
  createEncryptedTokenStorage,
  deriveKeyFromPRF,
  deriveKeyFromPassphrase,
} from '@drystuff/auth';
import { config } from '../config';

type Method = 'prf' | 'pin';
const VAULT_STORAGE_KEY = 'cnt_vault_enc'; // ciphertext (EncryptedTokenStorage)

function vaultCfg() {
  return config.auth.vault;
}

function randomSalt(): Uint8Array<ArrayBuffer> {
  const s = new Uint8Array(16);
  crypto.getRandomValues(s);
  return s;
}
function b64(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin);
}
function fromB64(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export interface SecureVault {
  isEnrolled(): boolean;
  enrolledMethod(): Method | null;
  enrollWithPrfOutput(prfOutput: ArrayBuffer, refreshToken: string): Promise<void>;
  unlockWithPrfOutput(prfOutput: ArrayBuffer): Promise<string>;
  enrollWithPin(pin: string, refreshToken: string): Promise<void>;
  unlockWithPin(pin: string): Promise<string>;
  wipe(): void;
}

export function createSecureVault(): SecureVault {
  const cfg = vaultCfg();

  function storageWith(getKey: () => Promise<CryptoKey>) {
    return createEncryptedTokenStorage({ getKey, storageKey: VAULT_STORAGE_KEY });
  }

  async function saveRefreshToken(getKey: () => Promise<CryptoKey>, refreshToken: string) {
    await storageWith(getKey).save({ accessToken: '', refreshToken });
  }
  async function loadRefreshToken(getKey: () => Promise<CryptoKey>): Promise<string> {
    const pair = await storageWith(getKey).load(); // throws on bad key
    const rt = pair?.refreshToken;
    if (!rt) throw new Error('Vault empty or undecryptable');
    return rt;
  }

  return {
    isEnrolled() {
      return !!localStorage.getItem(cfg.enrolledKey);
    },
    enrolledMethod() {
      return (localStorage.getItem(cfg.enrolledKey) as Method | null) ?? null;
    },

    async enrollWithPrfOutput(prfOutput, refreshToken) {
      await saveRefreshToken(() => deriveKeyFromPRF(prfOutput), refreshToken);
      localStorage.setItem(cfg.enrolledKey, 'prf');
      localStorage.removeItem(cfg.pinAttemptsKey);
    },

    async unlockWithPrfOutput(prfOutput) {
      return loadRefreshToken(() => deriveKeyFromPRF(prfOutput));
    },

    async enrollWithPin(pin, refreshToken) {
      const salt = randomSalt();
      localStorage.setItem(cfg.pinSaltKey, b64(salt));
      await saveRefreshToken(() => deriveKeyFromPassphrase(pin, salt), refreshToken);
      localStorage.setItem(cfg.enrolledKey, 'pin');
      localStorage.removeItem(cfg.pinAttemptsKey);
    },

    async unlockWithPin(pin) {
      const saltB64 = localStorage.getItem(cfg.pinSaltKey);
      if (!saltB64 || !this.isEnrolled()) throw new Error('Vault not enrolled for PIN');

      // If we've already used up every allowed attempt, lock the vault now. This
      // check runs on the attempt *after* the limit is reached, so a reload/crash
      // mid-decrypt can never reset the counter to dodge the wipe.
      const attempts = Number(localStorage.getItem(cfg.pinAttemptsKey) ?? '0');
      if (attempts >= cfg.maxPinAttempts) {
        this.wipe();
        throw new Error('Vault locked: too many PIN attempts');
      }
      // Persist the attempt BEFORE the slow PBKDF2 decrypt — count the intent so a
      // reload/crash still counts against the limit (fail-safe).
      localStorage.setItem(cfg.pinAttemptsKey, String(attempts + 1));

      const salt = fromB64(saltB64);
      const rt = await loadRefreshToken(() => deriveKeyFromPassphrase(pin, salt));
      localStorage.removeItem(cfg.pinAttemptsKey); // success resets counter
      return rt;
    },

    wipe() {
      localStorage.removeItem(VAULT_STORAGE_KEY);
      localStorage.removeItem(cfg.enrolledKey);
      localStorage.removeItem(cfg.pinSaltKey);
      localStorage.removeItem(cfg.pinAttemptsKey);
      localStorage.removeItem(cfg.prfCredentialKey);
    },
  };
}
